import type { paths } from './schema';

/**
 * A typed fetch wrapper for the Platform API (P0-PLAT-18).
 *
 * Deliberately small. Interceptors, retry policy, error mapping and the
 * organisation header's lifecycle belong to the portal (P0-WEB-04), which knows
 * about routing, toasts and the auth store; this package knows only what the
 * API is shaped like. Putting them here would make a generated-types package
 * into a second place application behaviour lives.
 *
 * What it does own is the shape: given a path and method that exist in the
 * spec, the body and the response are typed, and a path that does not exist is
 * a compile error rather than a 404 nobody sees until runtime.
 */

/** Every path in the spec. */
export type ApiPath = keyof paths;

/** The methods declared for one path. */
export type ApiMethod<P extends ApiPath> = keyof paths[P] & HttpMethod;

type HttpMethod = 'get' | 'post' | 'put' | 'patch' | 'delete';

type Operation<P extends ApiPath, M extends ApiMethod<P>> = paths[P][M];

type RequestBodyOf<P extends ApiPath, M extends ApiMethod<P>> =
  Operation<P, M> extends {
    requestBody: { content: { 'application/json': infer B } };
  }
    ? B
    : never;

/**
 * The success payload — the 2xx response with a JSON body.
 *
 * Failures are not in this type on purpose: they arrive as a thrown ApiError,
 * so a caller cannot forget to narrow a union and end up rendering an error
 * object as though it were data.
 *
 * 202 is included: queued work (a run submission) answers 202 with the run in
 * the body, and without it the payload typed as `never`.
 */
type SuccessOf<P extends ApiPath, M extends ApiMethod<P>> =
  Operation<P, M> extends {
    responses: infer R;
  }
    ? R extends Record<number, unknown>
      ? {
          [S in keyof R]: S extends 200 | 201 | 202
            ? R[S] extends { content: { 'application/json': infer T } }
              ? T
              : never
            : never;
        }[keyof R]
      : never
    : never;

type PathParams<P extends ApiPath, M extends ApiMethod<P>> =
  Operation<P, M> extends {
    parameters: { path: infer PP };
  }
    ? PP
    : undefined;

/**
 * The `?query` an operation declares, or `undefined` where it declares none.
 *
 * Taken from the spec rather than accepted as a loose record, for the same
 * reason the body is: a filter the API does not have is a parameter it silently
 * ignores, and a list that quietly returns everything looks like data rather
 * than like a bug.
 */
type QueryParams<P extends ApiPath, M extends ApiMethod<P>> =
  Operation<P, M> extends {
    parameters: { query?: infer Q };
  }
    ? Optionalise<Q>
    : undefined;

/**
 * Every value additionally allowed to be `undefined`.
 *
 * The repo runs `exactOptionalPropertyTypes`, under which a key present with
 * the value `undefined` is not the same as an absent key — so without this,
 * `{ q, owner: filters.owner }` is a type error whenever `owner` is optional,
 * and every call site has to prune its own object before calling. `buildQuery`
 * drops them at runtime, so accepting them here costs nothing.
 */
type Optionalise<Q> = { [K in keyof Q]?: Q[K] | undefined };

export interface ApiRequestOptions<P extends ApiPath, M extends ApiMethod<P>> {
  /** Values for the `{placeholders}` in the path. */
  path?: PathParams<P, M>;
  body?: RequestBodyOf<P, M>;
  /** Values for the `?name=value` parameters the operation declares. */
  query?: QueryParams<P, M>;
  /**
   * Which organisation the request acts for. Sent as X-Organisation-Id, which
   * the API checks against membership on every request (PRD §SEC-4) — it is a
   * claim, not a fact, so sending the wrong one fails rather than succeeding
   * against somebody else's data.
   */
  organisationId?: string;
  signal?: AbortSignal;
}

/**
 * A non-2xx response, with the status the caller needs to branch on.
 *
 * Thrown rather than returned so the success type above stays a single shape.
 * The portal maps these to messages (P0-WEB-04); nothing here decides what a
 * user is told, because that decision needs i18n and this package has none.
 */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly body: unknown,
    message?: string,
  ) {
    super(message ?? `The API responded ${status}.`);
    this.name = 'ApiError';
  }

  /** Field-keyed messages from a 422, if this was one. */
  get validationErrors(): Record<string, string[]> | undefined {
    if (this.status !== 422) return undefined;
    const body = this.body as { errors?: Record<string, string[]> } | null;
    return body?.errors;
  }
}

export interface ApiClientOptions {
  /** Where the API lives. No trailing slash. */
  baseUrl: string;
  /**
   * Injected so tests and server-side callers can supply their own. Defaults to
   * the global fetch.
   */
  fetch?: typeof globalThis.fetch;
}

export function createApiClient({ baseUrl, fetch = globalThis.fetch }: ApiClientOptions) {
  return async function request<P extends ApiPath, M extends ApiMethod<P>>(
    path: P,
    method: M,
    options: ApiRequestOptions<P, M> = {},
  ): Promise<SuccessOf<P, M>> {
    const url = baseUrl + fillPath(path, options.path) + buildQuery(options.query);

    const headers: Record<string, string> = {
      Accept: 'application/json',
      // Laravel needs this to answer 419/422 as JSON rather than a redirect.
      'X-Requested-With': 'XMLHttpRequest',
    };

    if (options.body !== undefined) headers['Content-Type'] = 'application/json';
    if (options.organisationId) headers['X-Organisation-Id'] = options.organisationId;

    // Built up rather than declared in one literal: the repo runs TypeScript
    // with exactOptionalPropertyTypes, under which `{ body: undefined }` is a
    // different thing from omitting `body` — and fetch rejects the former.
    const init: RequestInit = {
      method: method.toUpperCase(),
      headers,
      // The session is a cookie (P0-PLAT-07), so it only travels if credentials
      // are included. Omitting this is the mistake that makes every
      // authenticated request 401 with nothing obviously wrong in the code.
      credentials: 'include',
    };

    if (options.body !== undefined) init.body = JSON.stringify(options.body);
    if (options.signal) init.signal = options.signal;

    const response = await fetch(url, init);

    // 204 has no body to parse, and JSON.parse('') throws.
    const payload = response.status === 204 ? undefined : await readJson(response);

    if (!response.ok) {
      throw new ApiError(response.status, payload, messageFrom(payload));
    }

    return payload as SuccessOf<P, M>;
  };
}

export type ApiClient = ReturnType<typeof createApiClient>;

/**
 * `/v1/organisations/{organisation}/members` + `{organisation: 'abc'}`.
 *
 * Values are encoded, because a path parameter is user-supplied by the time it
 * reaches here even when it looks like a UUID.
 */
function fillPath(path: string, params?: unknown): string {
  if (!params) return path;

  return path.replace(/\{(\w+)\}/g, (whole, name: string) => {
    const value = (params as Record<string, string | number>)[name];
    return value === undefined ? whole : encodeURIComponent(value);
  });
}

/**
 * `?page=2&per_page=48`, or an empty string.
 *
 * Undefined and null values are dropped rather than sent as the strings
 * "undefined" and "null" — a caller spreading an optional filter into the
 * object is the normal way this is used, and the alternative is every call site
 * pruning its own object first.
 *
 * Arrays repeat the key (`?id=a&id=b`), which is what Laravel's
 * `array` validation rule reads.
 */
function buildQuery(query: unknown): string {
  if (query === undefined || query === null) return '';

  const params = new URLSearchParams();

  for (const [key, value] of Object.entries(query as Record<string, unknown>)) {
    if (value === undefined || value === null) continue;

    if (Array.isArray(value)) {
      for (const item of value) {
        const encoded = scalar(item);
        if (encoded !== undefined) params.append(key, encoded);
      }
      continue;
    }

    const encoded = scalar(value);
    if (encoded !== undefined) params.append(key, encoded);
  }

  const encoded = params.toString();
  return encoded === '' ? '' : `?${encoded}`;
}

/**
 * A query value as a string, or nothing.
 *
 * Objects are dropped rather than stringified: the spec declares only scalars
 * here, and `[object Object]` in a URL is a filter that matches nothing while
 * looking like it was applied.
 */
function scalar(value: unknown): string | undefined {
  if (value === undefined || value === null) return undefined;

  switch (typeof value) {
    case 'string':
      return value;
    case 'number':
    case 'boolean':
      return String(value);
    default:
      return undefined;
  }
}

async function readJson(response: Response): Promise<unknown> {
  const text = await response.text();
  if (text === '') return undefined;

  try {
    return JSON.parse(text);
  } catch {
    // A proxy error page or a truncated response. Returning the raw text keeps
    // it visible in the thrown ApiError instead of turning a bad gateway into a
    // JSON parse error with no context.
    return text;
  }
}

function messageFrom(payload: unknown): string | undefined {
  if (payload && typeof payload === 'object' && 'message' in payload) {
    const { message } = payload;
    if (typeof message === 'string') return message;
  }
  return undefined;
}
